import { useEffect, useState, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  FormFieldLabel,
  Input,
  NativeSelect,
} from '@eureca/ui';
import { PROFICIENCY_LEVELS } from './disabilities-seed';

export type SkillDialogMode =
  | { kind: 'select-level'; presetLabel: string; field: 'competencia' | 'diferencial' }
  | { kind: 'create'; field: 'competencia' | 'diferencial' };

export interface SkillSubmission {
  /** For preset mode this is the original preset label. For create it's the typed name. */
  name: string;
  level: string;
  isCustom: boolean;
}

export interface SkillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: SkillDialogMode | null;
  onSubmit: (value: SkillSubmission) => void;
}

const TITLES: Record<string, string> = {
  'select-level-competencia': 'Adicionar competência',
  'select-level-diferencial': 'Adicionar diferencial',
  'create-competencia': 'Adicionar nova competência',
  'create-diferencial': 'Adicionar novo diferencial',
};

export function SkillDialog({ open, onOpenChange, mode, onSubmit }: SkillDialogProps) {
  const [name, setName] = useState('');
  const [level, setLevel] = useState('');

  useEffect(() => {
    if (!open) {
      setName('');
      setLevel('');
    }
  }, [open]);

  if (!mode) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          aria-describedby={undefined}
          className="gap-0 p-0 lg:w-full lg:max-w-[423px] lg:rounded-xl"
        >
          <DialogHeader className="gap-1.5 px-6 pt-6">
            <DialogTitle>&nbsp;</DialogTitle>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const titleKey = `${mode.kind}-${mode.field}`;
  const title = TITLES[titleKey] ?? 'Adicionar';
  const isCreate = mode.kind === 'create';
  const canSubmit = level.trim().length > 0 && (!isCreate || name.trim().length > 0);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit || !mode) return;
    onSubmit({
      name: isCreate ? name.trim() : (mode as { presetLabel: string }).presetLabel,
      level: level.trim(),
      isCustom: isCreate,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 p-0 lg:w-full lg:max-w-[423px] lg:rounded-xl"
      >
        <DialogHeader className="gap-1.5 px-6 pt-6">
          <DialogTitle className="text-lg font-semibold leading-none tracking-[-0.025em] text-foreground">
            {title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-5 px-6 pt-3 pb-8">
            {isCreate ? (
              <FormField>
                <FormFieldLabel className="text-foreground">Nome</FormFieldLabel>
                <Input
                  autoFocus
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ex: Adobe Photoshop"
                  data-testid="skill-dialog-name"
                />
              </FormField>
            ) : null}
            <FormField>
              <FormFieldLabel className="text-foreground">
                {isCreate ? 'Nível de domínio' : 'Selecione o nível de domínio'}
              </FormFieldLabel>
              <NativeSelect
                value={level}
                onChange={(event) => setLevel(event.target.value)}
                data-testid="skill-dialog-level"
              >
                <option value="" disabled>
                  {isCreate ? 'Selecione um nível' : 'Selecione o nível de domínio'}
                </option>
                {PROFICIENCY_LEVELS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
          </div>
          <DialogFooter className="px-6 pb-6">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit} data-testid="skill-dialog-submit">
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function levelLabel(levelId: string): string {
  return PROFICIENCY_LEVELS.find((entry) => entry.id === levelId)?.label ?? levelId;
}
